import type { Rgba } from "./types";

const HEX_RE = /^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const NUM = String.raw`(\d+(?:\.\d+)?|\.\d+)`;
// Each channel is a number or a percentage, as CSS allows.
const RGB_RE = new RegExp(
  String.raw`^rgba?\(\s*${NUM}(%?)[\s,]+${NUM}(%?)[\s,]+${NUM}(%?)(?:[\s,/]+${NUM}(%?))?\s*\)$`,
  "i",
);
const BYTE_MAX = 255;
const PERCENT_MAX = 100;

const clampByte = (n: number) => Math.min(255, Math.max(0, Math.round(n)));
const clampUnit = (n: number) => Math.min(1, Math.max(0, n));

/** Parses hex and rgb()/rgba(); anything else (names, var(), hsl) returns null. */
export const parseColor = (input: string | undefined | null): Rgba | null => {
  if (!input) {
    return null;
  }
  const value = input.trim();
  const hex = HEX_RE.exec(value);
  if (hex) {
    let digits = hex[1];
    if (digits.length <= 4) {
      digits = [...digits].map((d) => d + d).join("");
    }
    const n = (i: number) => parseInt(digits.slice(i, i + 2), 16);
    return {
      r: n(0),
      g: n(2),
      b: n(4),
      a: digits.length === 8 ? n(6) / 255 : 1,
    };
  }
  const rgb = RGB_RE.exec(value);
  if (rgb) {
    const channel = (value: string, unit: string) =>
      unit ? (Number(value) * BYTE_MAX) / PERCENT_MAX : Number(value);
    const r = channel(rgb[1], rgb[2]);
    const g = channel(rgb[3], rgb[4]);
    const b = channel(rgb[5], rgb[6]);
    const alpha = rgb[7];
    const a = alpha === undefined ? 1 : Number(alpha) / (rgb[8] ? 100 : 1);
    return {
      r: clampByte(r),
      g: clampByte(g),
      b: clampByte(b),
      a: clampUnit(a),
    };
  }
  return null;
};

// A closed list rather than /[a-z]+/: letter-only hex missing its "#"
// ("ffffff", "fafafa") and typos would otherwise pass as names.
const NAMED_COLORS = new Set(
  (
    "aliceblue antiquewhite aqua aquamarine azure beige bisque black " +
    "blanchedalmond blue blueviolet brown burlywood cadetblue chartreuse " +
    "chocolate coral cornflowerblue cornsilk crimson cyan darkblue darkcyan " +
    "darkgoldenrod darkgray darkgreen darkgrey darkkhaki darkmagenta " +
    "darkolivegreen darkorange darkorchid darkred darksalmon darkseagreen " +
    "darkslateblue darkslategray darkslategrey darkturquoise darkviolet " +
    "deeppink deepskyblue dimgray dimgrey dodgerblue firebrick floralwhite " +
    "forestgreen fuchsia gainsboro ghostwhite gold goldenrod gray green " +
    "greenyellow grey honeydew hotpink indianred indigo ivory khaki lavender " +
    "lavenderblush lawngreen lemonchiffon lightblue lightcoral lightcyan " +
    "lightgoldenrodyellow lightgray lightgreen lightgrey lightpink " +
    "lightsalmon lightseagreen lightskyblue lightslategray lightslategrey " +
    "lightsteelblue lightyellow lime limegreen linen magenta maroon " +
    "mediumaquamarine mediumblue mediumorchid mediumpurple mediumseagreen " +
    "mediumslateblue mediumspringgreen mediumturquoise mediumvioletred " +
    "midnightblue mintcream mistyrose moccasin navajowhite navy oldlace olive " +
    "olivedrab orange orangered orchid palegoldenrod palegreen paleturquoise " +
    "palevioletred papayawhip peachpuff peru pink plum powderblue purple " +
    "rebeccapurple red rosybrown royalblue saddlebrown salmon sandybrown " +
    "seagreen seashell sienna silver skyblue slateblue slategray slategrey " +
    "snow springgreen steelblue tan teal thistle tomato turquoise violet " +
    "wheat white whitesmoke yellow yellowgreen transparent currentcolor"
  ).split(" "),
);

// Arguments may nest one level (color-mix(in srgb, var(--a) 40%, white)) but
// never contain declaration or string syntax, so a value can't close its
// function early and smuggle in another layer such as url().
const ARG = String.raw`[^();{}<>"'\\]`;
const COLOR_FUNCTION_RE = new RegExp(
  String.raw`^(?:hsla?|hwb|lab|lch|oklab|oklch|color|color-mix|var)\((?:${ARG}|\(${ARG}*\))*\)$`,
  "i",
);
const URL_RE = /url\s*\(/i;

const isPassthroughColor = (value: string): boolean =>
  NAMED_COLORS.has(value.toLowerCase()) ||
  (COLOR_FUNCTION_RE.test(value) && !URL_RE.test(value));

/**
 * `color` when it could be a valid CSS colour, else `fallback`. A malformed
 * value ("4f87ff", "ffffff", "#12345") would otherwise poison every
 * color-mix() derived from it and blank out the surfaces it tints. Syntactic
 * only, so server and client renders agree.
 */
export const paintableColor = (
  color: string | undefined | null,
  fallback: string,
): string => {
  const value = color?.trim();
  if (!value) {
    return fallback;
  }
  return parseColor(value) || isPassthroughColor(value) ? value : fallback;
};

const format = ({ r, g, b, a }: Rgba) =>
  `rgba(${clampByte(r)}, ${clampByte(g)}, ${clampByte(b)}, ${Number(clampUnit(a).toFixed(3))})`;

const percent = (t: number) => `${Number((clampUnit(t) * 100).toFixed(1))}%`;

/**
 * `color` at `alpha` opacity. Parseable colours resolve to rgba() so inline
 * styles stay plain; anything else (a CSS variable, a named colour) becomes a
 * color-mix() the browser resolves.
 */
export const withAlpha = (color: string, alpha: number): string => {
  const parsed = parseColor(color);
  if (parsed) {
    return format({ ...parsed, a: parsed.a * alpha });
  }
  return `color-mix(in srgb, ${color} ${percent(alpha)}, transparent)`;
};

/** `a` blended toward `b` by `t` (0 = a, 1 = b), fully opaque. */
export const mix = (a: string, b: string, t: number): string => {
  const pa = parseColor(a);
  const pb = parseColor(b);
  if (pa && pb) {
    const k = clampUnit(t);
    return format({
      r: pa.r + (pb.r - pa.r) * k,
      g: pa.g + (pb.g - pa.g) * k,
      b: pa.b + (pb.b - pa.b) * k,
      a: 1,
    });
  }
  return `color-mix(in srgb, ${a} ${percent(1 - t)}, ${b})`;
};

/**
 * `color` composited over `base`, so a translucent value becomes the solid
 * colour it would look like there. Unparseable input is returned unchanged.
 */
export const opaqueOver = (color: string, base: string): string => {
  const pc = parseColor(color);
  const pb = parseColor(base);
  if (!pc || !pb) {
    return color;
  }
  return mix(base, format({ ...pc, a: 1 }), pc.a);
};
